// useRescission.ts
import { useEffect, useMemo, useState } from "react";
import { useFormik } from "formik";
import { differenceInDays } from "date-fns";
import { getRenterRentalHistory, cancelSubscription } from "@/common/services/renters.api";
import { IRenterRentalHistory } from "@/types/renter-rental-history";
import { IRenter } from "@/types/renter.type";
import { BILLING_TYPE, PaymentInfo } from "@/types/payment.type";
import { useToastContext } from "@/common/components/ui/toast/toast.context";
import { calculateCreditCardInstallmentValue, getCreditCardFees } from "../../negotiation-debits/helpers/payment-rules.helpers";
import { useCancelPlan } from "../../modals/fields/EditBondRenterVehcile/formsModal/customHook/useCancelPlan";
import { getRenterPayments } from "@/common/services/payments.api";
import { getVisualRecords, getVisualRecordsById, getPartnersByLicensePlate, PartnerByLicensePlate } from "@/common/services/vehicles.api";
import { IVehicleVisualRecord } from "@/types/vehicle-visual-record.type";
import { getRentalHistoryWithFallback, convertDesactivationToRentalHistory } from "../service/repossession-renter";

interface UseRescissionProps {
  renter: IRenter;
}

export const useRescission = ({ renter }: UseRescissionProps) => {
  const { notifySuccess, notifyError } = useToastContext();
  const [isLoading, setIsLoading] = useState(true);
  const [additionalValues, setAdditionalValues] = useState(0);
  const [vehicleVisualRecords, setVehicleVisualRecords] = useState<IVehicleVisualRecord | null>(null);
  const [rentalHistory, setRentalHistory] = useState<IRenterRentalHistory[]>([]);
  const [payments, setPayments] = useState<PaymentInfo[]>([]);
  const [manualAdjustment, setManualAdjustment] = useState(0);
  const [isAddingAdjustment, setIsAddingAdjustment] = useState(true);
  const [selectedPayments, setSelectedPayments] = useState<string[]>([]);
  const [partnersMap, setPartnersMap] = useState<Record<string, PartnerByLicensePlate>>({});
  const [loadingPartners, setLoadingPartners] = useState(false);
  const renterlRenterHistoryVehicle = renter?.desactivationData?.vehicleHistory;

  const vehicleId = rentalHistory[0]?.vehicleId
    || renterlRenterHistoryVehicle?.[renterlRenterHistoryVehicle.length - 1]?.id
    || '';

  const daysRemaining = useMemo(() => {
    return Math.abs(
      differenceInDays(
        rentalHistory[0]?.unlinkedAt ? new Date(rentalHistory[0]?.unlinkedAt) : new Date(),
        renter.activationData?.plan.subscriptionEndDate || new Date()
      )
    );
  }, [rentalHistory, renter.activationData?.plan.subscriptionEndDate]);

  const finePlanValue = useMemo(() => {
    return (Number(renter.activationData?.plan.rawValue) * daysRemaining * 0.10).toFixed(2);
  }, [renter.activationData?.plan.rawValue, daysRemaining]);

  // useCancelPlan para obter valores e handlers
  const {
    isChecked,
    caucao,
    valueWeeks,
    dailyValue,
    paymentOptions,
    cancellationReasonOptions,
    installmentOptions,
    handleTotalChange,
    formik: cancelPlanFormik,
  } = useCancelPlan({
    renter,
    vehicleId,
    daysRemaining: daysRemaining,
  });

  useEffect(() => {
    async function fetchRentalHistory(renterId: string) {
      if (!renterId) return;

      try {
        setIsLoading(true);
        setRentalHistory([]);
        setPayments([]);

        // Busca histórico da API
        const response = await getRenterRentalHistory(renterId);


        const filteredData = response.data?.filter(
          (history: IRenterRentalHistory) => history.status === 'finalized'
        ) || [];

        const historyWithFallback = await getRentalHistoryWithFallback(
          renter,
          filteredData
        );

        setRentalHistory(historyWithFallback);

        const { data, ok } = await getRenterPayments(renterId);

        if (ok) {
          const sortedPayments = data.sort((a, b) => {
            const dateB = new Date(b.dueDate!);
            const dateA = new Date(a.dueDate!);
            return dateB.getTime() - dateA.getTime();
          });

          const filteredPayments = sortedPayments.filter(payment =>
            payment.status !== "RECEIVED" && payment.status !== "Inativo"
          );

          setPayments(filteredPayments as unknown as PaymentInfo[]);
        }
      } catch (error) {
        console.error("Erro ao buscar histórico de locações:", error);

        const fallbackHistory = convertDesactivationToRentalHistory(renter);
        setRentalHistory(fallbackHistory);
      } finally {
        setIsLoading(false);
      }
    }

    if (renter._id) fetchRentalHistory(renter._id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renter._id]);

  useEffect(() => {
    async function getPhotosRepossesion() {
      try {
        setVehicleVisualRecords(null);

        const lastRental = rentalHistory[rentalHistory.length - 1];
        const visualRecordId = lastRental?.vehicleVisualRecordId;

        if (visualRecordId) {
          const response = await getVisualRecordsById(visualRecordId);
          setVehicleVisualRecords(response.data);
        }

        if (!renter._id || !vehicleId) return;

        const searchData = {
          vehicleId: vehicleId,
          renterId: renter._id,
        };

        const { data, ok } = await getVisualRecords(searchData);

        if (ok && data) {
          const sortedDataWithPhotos = data.sort(
            (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          ) as IVehicleVisualRecord[];

          setVehicleVisualRecords(sortedDataWithPhotos[0]);
        }
      } catch (error) {
        console.error('Erro ao buscar fotos de recolhimento:', error);
      }
    }

    if (rentalHistory.length > 0 && vehicleId) {
      getPhotosRepossesion();
    }
  }, [rentalHistory, vehicleId, renter._id]);

  // Buscar partners dos veículos do histórico
  useEffect(() => {
    const fetchPartners = async () => {
      if (!renter.desactivationData?.vehicleHistory) return;

      const licensePlates = renter.desactivationData.vehicleHistory
        .filter((v) => v.licensePlate && v.licensePlate.trim() !== '')
        .map((v) => v.licensePlate);

      if (licensePlates.length === 0) return;

      setLoadingPartners(true);
      try {
        const result = await getPartnersByLicensePlate(licensePlates);

        if (result.ok && result.data) {
          // Criar mapa de placa -> partner
          const map: Record<string, PartnerByLicensePlate> = {};
          if (Array.isArray(result.data)) {
            result.data.forEach((item) => {
              if (item.licensePlate && item.partnerCompanyName) {
                map[item.licensePlate] = {
                  vehicleId: item.vehicleId || '',
                  licensePlate: item.licensePlate,
                  partnerCompanyName: item.partnerCompanyName
                };
              }
            });
          }
          setPartnersMap(map);
        }
      } catch (error) {
        console.error('Erro ao buscar partners:', error);
      } finally {
        setLoadingPartners(false);
      }
    };

    fetchPartners();
  }, [renter.desactivationData?.vehicleHistory]);

  const handleTotalChangeWrapper = (values: {
    initalValue: number;
    amountValue: number;
    isCheckedRetraction: boolean;
    isCheckedCommandBlock: boolean;
    valuesSinister: number;
    isPostWeekChecked: boolean;
    isWeeklyLate: boolean;
  }) => {
    setAdditionalValues(values.amountValue);
    handleTotalChange(values);
  };

  const damagedPartsTotal = useMemo(() => {
    if (!rentalHistory[0]?.damagedParts) return 0;
    return rentalHistory[0].damagedParts.reduce((sum, part) => {
      return sum + (part.quantity * (part.sellPrice || 0));
    }, 0);
  }, [rentalHistory]);

  const selectedPaymentsTotal = useMemo(() => {
    return payments
      .filter(payment => selectedPayments.includes(payment._id!))
      .reduce((sum, payment) => sum + Number(payment.value || 0), 0);
  }, [payments, selectedPayments]);

  const amountValue = useMemo(() => {
    const multa = Number(finePlanValue);
    const caucaoValue = caucao ? Number(renter.activationData?.plan.deposit || 0) : 0;
    const adjustmentValue = isAddingAdjustment ? manualAdjustment : -manualAdjustment;
    const total = multa + damagedPartsTotal + additionalValues - caucaoValue + adjustmentValue + selectedPaymentsTotal;
    return total.toFixed(2);
  }, [finePlanValue, damagedPartsTotal, additionalValues, caucao, renter.activationData?.plan.deposit, manualAdjustment, isAddingAdjustment, selectedPaymentsTotal]);

  const paymentFormik = useFormik({
    initialValues: {
      billingType: BILLING_TYPE.UNDEFINED,
      billingPeriod: "",
      reason: "",
      additionalComments: "",
      paymentDescription: "",
      dueDate: new Date(),
      creditCard: {
        hasEntry: false,
        entryPercentage: 0,
        entryValue: 0,
        installments: 1,
        cardValue: 0,
        finalCardValue: 0,
        installmentValue: 0,
        fees: {
          rate: 0,
          fixed: 0,
          description: ""
        },
        totalGeneral: 0
      },
      boletoOrPix: {
        entryPercentage: 0,
        entryValue: 0,
        installments: 1,
        remainingValue: 0,
        installmentValue: 0,
        hasInstallments: false,
        entryPercentageText: "",
        installmentsText: "",
        nextDueDate: ""
      }
    },
    onSubmit: async (values) => {
      try {
        // Calcular o valor total correto
        const totalValue = parseFloat(amountValue);

        // Preparar dados base
        const baseData = {
          renterId: renter._id,
          vehicleId: vehicleId,
          customerId: renter.externalReferences.paymentCustomerId,

          // Dados de rescisão
          reason: values.reason || "PLAN_CANCELLATION",
          additionalComments: values.additionalComments,
          paymentDescription: values.paymentDescription,
          dueDate: values.dueDate,

          // Valores financeiros
          totalValue: totalValue, // Valor total da cobrança
          finalFineValue: cancelPlanFormik.values.finalFineValue,
          amountValue: totalValue, // Valor total incluindo todos os ajustes
          caucao: caucao?.toString(),
          dailyValue: dailyValue,
          finePlanValue: parseFloat(finePlanValue),
          valueBilling: totalValue,

          // Flags adicionais
          isCheckedRetraction: cancelPlanFormik.values.isCheckedRetraction,
          isCheckedCommandBlock: cancelPlanFormik.values.isCheckedCommandBlock,
          sinister: cancelPlanFormik.values.sinister,
          isPostWeekChecked: cancelPlanFormik.values.isPostWeekChecked,
          isWeeklyLate: cancelPlanFormik.values.isWeeklyLate,
          damagedPartsTotal: damagedPartsTotal,

          // Dados de pagamento
          paymentMethod: values.billingType,
          billingPeriod: values.billingPeriod,
          subscriptionInitialDate: new Date(),
        };

        // Adicionar dados específicos de cada método de pagamento
        let cancelData;

        if (values.billingType === 'CREDIT_CARD') {
          cancelData = {
            ...baseData,
            // Dados do cartão de crédito
            installmentOptions: values.creditCard.installments.toString(),
            installmentValue: values.creditCard.installmentValue.toString(),
            hasEntry: values.creditCard.hasEntry,
            entryPercentage: values.creditCard.entryPercentage,
            entryValue: values.creditCard.entryValue,
            cardValue: values.creditCard.cardValue,
            finalCardValue: values.creditCard.finalCardValue,
            totalGeneral: values.creditCard.totalGeneral,
            fees: values.creditCard.fees,
          };
        } else if (['PIX', 'BOLETO'].includes(values.billingType)) {
          cancelData = {
            ...baseData,
            // Dados de PIX/Boleto
            installmentOptions: values.boletoOrPix.installments.toString(),
            installmentValue: values.boletoOrPix.installmentValue.toFixed(2).toString(),
            entryPercentage: values.boletoOrPix.entryPercentage,
            entryValue: values.boletoOrPix.entryValue,
            remainingValue: values.boletoOrPix.remainingValue,
            hasInstallments: values.boletoOrPix.hasInstallments,
            entryPercentageText: values.boletoOrPix.entryPercentageText,
            installmentsText: values.boletoOrPix.installmentsText,
            nextDueDate: values.boletoOrPix.nextDueDate,
          };
        } else {
          cancelData = {
            ...baseData,
            installmentOptions: "1",
            installmentValue: totalValue.toString(),
          };
        }

        const result = await cancelSubscription(cancelData);

        if (!result.ok) {
          if (Array.isArray(result.message)) {
            result.message.forEach((message: string) => {
              notifyError(message);
            });
          } else {
            notifyError(result.message as string);
          }
          return;
        }

        notifySuccess("Cobrança de rescisão gerada com sucesso!");

        // TODO: Redirecionar ou atualizar página
      } catch (error) {
        console.error("Erro ao gerar cobrança:", error);
        notifyError("Erro ao gerar cobrança de rescisão");
      }
    }
  });

  // Calcular valores do Cartão de Crédito
  useEffect(() => {
    if (paymentFormik.values.billingType === 'CREDIT_CARD') {
      const totalValue = parseFloat(amountValue);
      const entryPercentage = paymentFormik.values.creditCard.entryPercentage;
      const installments = paymentFormik.values.creditCard.installments;

      const entryValue = (entryPercentage / 100) * totalValue;
      const cardValue = entryPercentage > 0 ? totalValue - entryValue : totalValue;

      const fees = getCreditCardFees(installments);
      const monthlyValueWithFees = calculateCreditCardInstallmentValue(cardValue, installments);
      const finalCardValue = monthlyValueWithFees * installments;

      const totalGeneral = entryValue + finalCardValue;

      paymentFormik.setFieldValue('creditCard.entryValue', entryValue);
      paymentFormik.setFieldValue('creditCard.cardValue', cardValue);
      paymentFormik.setFieldValue('creditCard.finalCardValue', finalCardValue);
      paymentFormik.setFieldValue('creditCard.installmentValue', monthlyValueWithFees);
      paymentFormik.setFieldValue('creditCard.totalGeneral', totalGeneral);
      paymentFormik.setFieldValue('creditCard.fees', fees);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    paymentFormik.values.billingType,
    paymentFormik.values.creditCard.entryPercentage,
    paymentFormik.values.creditCard.installments,
    amountValue
  ]);

  useEffect(() => {
    if (['PIX', 'BOLETO'].includes(paymentFormik.values.billingType)) {
      const totalValue = parseFloat(amountValue);
      const rawEntryPercentage = paymentFormik.values.boletoOrPix.entryPercentage;
      const installments = Number(paymentFormik.values.boletoOrPix.installments) || 1;

      const pct = Number(rawEntryPercentage) || 0;

      let entryValue = 0;
      if (pct > 0) {
        entryValue = Math.max((pct / 100) * totalValue, 50);
      }

      let remainingValue = totalValue - entryValue;
      if (remainingValue < 0) remainingValue = 0;

      const hasInstallments = installments > 1 && remainingValue >= 50;
      const installmentValue = hasInstallments ? remainingValue / installments : 0;

      const entryPercentageText = `${pct}% (entrada R$ ${entryValue.toFixed(2)})`;
      const installmentsText = hasInstallments
        ? `${installments}x de R$ ${installmentValue.toFixed(2)}`
        : "À vista";

      paymentFormik.setFieldValue('boletoOrPix.entryValue', entryValue);
      paymentFormik.setFieldValue('boletoOrPix.remainingValue', remainingValue);
      paymentFormik.setFieldValue('boletoOrPix.installmentValue', installmentValue);
      paymentFormik.setFieldValue('boletoOrPix.hasInstallments', hasInstallments);
      paymentFormik.setFieldValue('boletoOrPix.entryPercentageText', entryPercentageText);
      paymentFormik.setFieldValue('boletoOrPix.installmentsText', installmentsText);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    paymentFormik.values.billingType,
    paymentFormik.values.boletoOrPix.entryPercentage,
    paymentFormik.values.boletoOrPix.installments,
    amountValue
  ]);

  // Função auxiliar para extrair números de infração
  const extractInfractionNumbers = (description: string): string[] => {
    const regex = /(?:AUTO DE INFRAÇÃO Nº:\s*|Nº:\s*)([A-Z]{2}\d{8})/gi;
    const matches: string[] = [];
    let match;

    while ((match = regex.exec(description)) !== null) {
      matches.push(match[1]);
    }

    return matches;
  };

  // Atualizar descrição automaticamente
  useEffect(() => {
    const items: string[] = [];

    // Quebra contratual
    items.push(`Quebra contratual R$ ${finePlanValue}`);

    // Adicionar valores adicionais com seus valores
    if (cancelPlanFormik.values.isPostWeekChecked && valueWeeks > 0) {
      items.push(`Semana Pós R$ ${valueWeeks.toFixed(2)}`);
    }

    if (cancelPlanFormik.values.isWeeklyLate && valueWeeks > 0) {
      items.push(`Semanal R$ ${valueWeeks.toFixed(2)}`);
    }

    if (cancelPlanFormik.values.isCheckedCommandBlock) {
      items.push(`Bloqueio R$ 20,00`);
    }

    if (cancelPlanFormik.values.isCheckedRetraction) {
      items.push(`Recolhimento R$ 200,00`);
    }

    // Avaria
    if (damagedPartsTotal > 0) {
      items.push(`Avaria R$ ${damagedPartsTotal.toFixed(2)}`);
    }

    if (cancelPlanFormik.values.sinister > 0) {
      items.push(`Sinistro R$ ${cancelPlanFormik.values.sinister.toFixed(2)}`);
    }

    // Pagamentos selecionados - agrupar infrações
    const selectedPaymentsList = payments.filter(payment => selectedPayments.includes(payment._id!));

    // Separar infrações de outros pagamentos
    const infractions: { number: string; value: number }[] = [];
    const otherPayments: { description: string; value: number }[] = [];

    selectedPaymentsList.forEach(payment => {
      const infractionNumbers = extractInfractionNumbers(payment.description || '');

      if (infractionNumbers.length > 0) {
        infractionNumbers.forEach(number => {
          infractions.push({
            number,
            value: Number(payment.value || 0)
          });
        });
      } else {
        otherPayments.push({
          description: payment.description || 'Pagamento',
          value: Number(payment.value || 0)
        });
      }
    });

    // Adicionar infrações agrupadas
    if (infractions.length > 0) {
      const infractionNumbers = infractions.map(i => i.number).join('/ ');
      const infractionTotal = infractions.reduce((sum, i) => sum + i.value, 0);
      items.push(`Infração ${infractionNumbers} R$ ${infractionTotal.toFixed(2)}`);
    }

    // Adicionar outros pagamentos
    otherPayments.forEach(payment => {
      const shortDesc = payment.description.substring(0, 20);
      items.push(`${shortDesc} R$ ${payment.value.toFixed(2)}`);
    });

    // Ajuste manual
    if (manualAdjustment > 0) {
      const adjustSign = isAddingAdjustment ? '+' : '-';
      const adjustLabel = isAddingAdjustment ? 'Ajuste' : 'Desconto';
      items.push(`${adjustLabel} R$ ${adjustSign}${manualAdjustment.toFixed(2)}`);
    }

    // Desconto do caução
    if (caucao && caucao > 0) {
      items.push(`Menos Caução R$ ${caucao.toFixed(2)}`);
    }

    // Total
    items.push(`Total R$ ${amountValue}`);

    // Montar descrição com limite de caracteres
    let description = items.join(' / ');

    // Se exceder 500 caracteres, resumir
    if (description.length > 500) {
      // Tentar versão mais curta
      const shortItems: string[] = [];
      shortItems.push(`Quebra R$ ${finePlanValue}`);

      if (damagedPartsTotal > 0) {
        shortItems.push(`Avaria R$ ${damagedPartsTotal.toFixed(2)}`);
      }

      if (infractions.length > 0) {
        const infractionTotal = infractions.reduce((sum, i) => sum + i.value, 0);
        shortItems.push(`Infração(${infractions.length}) R$ ${infractionTotal.toFixed(2)}`);
      }

      if (selectedPaymentsList.length - infractions.length > 0) {
        shortItems.push(`Outros(${selectedPaymentsList.length - infractions.length})`);
      }

      if (caucao && caucao > 0) {
        shortItems.push(`-Caução R$ ${caucao.toFixed(2)}`);
      }

      shortItems.push(`Total R$ ${amountValue}`);
      description = shortItems.join(' / ');

      // Se ainda exceder, truncar
      if (description.length > 500) {
        description = description.substring(0, 497) + '...';
      }
    }

    // Atualizar apenas se houver mudança
    if (paymentFormik.values.paymentDescription !== description) {
      paymentFormik.setFieldValue('paymentDescription', description);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    finePlanValue,
    damagedPartsTotal,
    additionalValues,
    cancelPlanFormik.values.isPostWeekChecked,
    cancelPlanFormik.values.isWeeklyLate,
    cancelPlanFormik.values.isCheckedCommandBlock,
    cancelPlanFormik.values.isCheckedRetraction,
    cancelPlanFormik.values.sinister,
    caucao,
    amountValue,
    selectedPayments,
    payments,
    manualAdjustment,
    isAddingAdjustment,
    valueWeeks
  ]);

  const handleTogglePayment = (paymentId: string) => {
    setSelectedPayments(prev => {
      if (prev.includes(paymentId)) {
        return prev.filter(id => id !== paymentId);
      }
      return [...prev, paymentId];
    });
  };

  const handleManualAdjustmentChange = (value: number) => {
    setManualAdjustment(value);
  };

  const handleToggleAdjustmentType = () => {
    setIsAddingAdjustment(prev => !prev);
  };

  return {
    // Estados
    isLoading,
    rentalHistory,
    additionalValues,
    manualAdjustment,
    isAddingAdjustment,
    selectedPayments,
    selectedPaymentsTotal,
    partnersMap,
    loadingPartners,

    // Valores calculados
    daysRemaining,
    finePlanValue,
    damagedPartsTotal,
    amountValue,
    payments,
    vehicleVisualRecords,

    // Handlers
    handleTotalChangeWrapper,
    handleTogglePayment,
    handleManualAdjustmentChange,
    handleToggleAdjustmentType,

    // Formik
    paymentFormik,
    cancelPlanFormik,

    // useCancelPlan
    isChecked,
    caucao,
    valueWeeks,
    dailyValue,
    paymentOptions,
    cancellationReasonOptions,
    installmentOptions,
  };
};